/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Status → tone, per Real Estate vocabulary.
 *
 * Tones resolve to the semantic tokens in styles/tokens/status.js. The same
 * word can mean different things in different vocabularies (a DRAFT payout is
 * neutral, an ACTIVE hold is a warning because it will expire), so the map is
 * keyed by vocabulary rather than by bare status.
 */

import statusTokens from 'styles/tokens/status';

export const TONES = statusTokens;

export const STATUS_TONES = {
  project: {
    PLANNED: 'neutral', LAUNCHED: 'info', UNDER_CONSTRUCTION: 'warning', COMPLETED: 'success',
    HANDED_OVER: 'success', ON_HOLD: 'error',
  },
  unit: {
    AVAILABLE: 'success', HELD: 'warning', RESERVED: 'info', BOOKED: 'info', SOLD: 'neutral',
    HANDED_OVER: 'neutral', LEASED: 'info', BLOCKED: 'error',
  },
  viewing: {
    SCHEDULED: 'info', CONFIRMED: 'info', COMPLETED: 'success', NO_SHOW: 'error', CANCELLED: 'neutral',
  },
  hold: { ACTIVE: 'warning', CONVERTED: 'success', EXPIRED: 'neutral', RELEASED: 'neutral' },
  booking: {
    DRAFT: 'neutral', RESERVED: 'warning', SPA_ISSUED: 'info', SPA_SIGNED: 'info', REGISTERED: 'success',
    HANDED_OVER: 'success', CANCELLED: 'error',
    // Australia (variants/au.py)
    CONTRACT_EXCHANGED: 'info', UNCONDITIONAL: 'info', SETTLED: 'success',
  },
  installment: {
    PENDING_TRIGGER: 'neutral', SCHEDULED: 'info', DUE: 'warning', PARTIALLY_PAID: 'warning', PAID: 'success',
    OVERDUE: 'error', WAIVED: 'neutral', CANCELLED: 'neutral',
  },
  commission: {
    ACCRUED: 'neutral', ELIGIBLE: 'info', APPROVED: 'warning', PAID: 'success', CLAWED_BACK: 'error',
    CANCELLED: 'neutral',
  },
  payout: { DRAFT: 'neutral', APPROVED: 'warning', PAID: 'success', CANCELLED: 'error' },
  lease: {
    DRAFT: 'neutral', ACTIVE: 'success', RENEWAL_DUE: 'warning', RENEWED: 'info', TERMINATED: 'error',
    EXPIRED: 'neutral',
  },
  cheque: {
    PENDING: 'neutral', DEPOSITED: 'info', CLEARED: 'success', BOUNCED: 'error', REPLACED: 'warning',
    CANCELLED: 'neutral',
  },
  mortgage: {
    ENQUIRY: 'neutral', DOCUMENTS: 'info', PRE_APPROVAL: 'info', VALUATION: 'warning', FINAL_OFFER: 'warning',
    DISBURSED: 'success', DECLINED: 'error', WITHDRAWN: 'neutral',
  },
  interest: { HOT: 'error', WARM: 'warning', COLD: 'info' },
};

export const toneFor = (vocab, status) => (STATUS_TONES[vocab] && STATUS_TONES[vocab][status]) || 'neutral';

/**
 * Inventory grid needs eight visually distinct unit colours, more than the
 * five semantic tones, so it has its own palette (fill, text, border).
 */
export const UNIT_TILE_COLORS = {
  AVAILABLE: { bg: '#D7F0E0', fg: '#14532D', border: '#1F8A56' },
  HELD: { bg: '#FFE9C7', fg: '#7A4A00', border: '#E68A06' },
  RESERVED: { bg: '#D6E3FF', fg: '#0B3D91', border: '#1565C8' },
  BOOKED: { bg: '#E3DAFF', fg: '#3B1F8A', border: '#6B4BD8' },
  SOLD: { bg: '#DDE3EC', fg: '#26344A', border: '#5C6B82' },
  HANDED_OVER: { bg: '#C9D2E0', fg: '#1B2638', border: '#3E4C63' },
  LEASED: { bg: '#CFF1F4', fg: '#0B4F57', border: '#1597A6' },
  BLOCKED: { bg: '#FFDAD6', fg: '#8A1111', border: '#BA1A1A' },
};
